import { Component, AfterViewInit, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { GenericResponseWithTotal } from '../interfaces/generic.interface';
import { Partner, PartnerGroup } from '../interfaces/partners.interface';
import { PartnersService } from '../services/partners.service';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs';
import { ScrollPositionService } from '../services/scroll-position.service';


@Component({
  selector: 'app-partners',
  standalone: false,
  templateUrl: './partners.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./partners.component.scss']
})
export class PartnersComponent implements OnInit, AfterViewInit {
  selectedGroup?: number | null;
  groups: PartnerGroup[] = [];
  categoryFromUrl?: string;

  private _allPartners: any[] = [];

  items$: Observable<GenericResponseWithTotal<Partner>> | null = null; 
  imagesUrl = `${environment.ImagesUrl}/partners/`;


  constructor(
    private partnerService: PartnersService,
    private router: Router,
    private route: ActivatedRoute,
    private scrollPositionService: ScrollPositionService
  ) {
    // Subscribe to route parameters
    this.route.params.subscribe(params => {
      this.categoryFromUrl = params['categoryName'];
      if (!params['categoryName']) {
        // No category in URL, use saved selection
        this.selectedGroup = Number(localStorage.getItem('offerMenuGroupId'));
      }
      // If there is a category in URL, wait for groups to load to find the matching ID
    });
  }


  onGroupChange(group: number | null) {
    this.selectedGroup = group;
    this.getFilteredPartners(group);
  }
    
  goToPartnerDetails(id: number, name?: string) {
    let urlName = name
      ? name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
      : 'partner';
    
    // Use the scroll position service with clicked item ID
    const route = ['/partner', urlName, id.toString()];
    const currentRoute = this.router.url; // Use actual current route instead of hardcoded '/partnerzy'
    this.scrollPositionService.navigateWithScrollSave(route, currentRoute, id.toString());
  }

  getFilteredPartners(category_id: number | null): void {
    this.items$ = this.partnerService.getData({is_active: 1, category_id: category_id, parent_id: 0, order_by: 'sort', order: 'desc', limit: 10000});  
  }

  ngOnInit() {
    // Load groups first to be able to match URL categories
    this.loadGroups();
  }

  loadGroups(): void {
    this.partnerService.getPartnerGroup().subscribe({
      next: (groups) => {
        this.groups = groups;
        
        // Now that groups are loaded, process the URL category if present
        if (this.categoryFromUrl) {
          this.findAndSelectGroupByName(this.categoryFromUrl);
        } else {
          // No category in URL, use saved selection or load all
          this.getFilteredPartners(this.selectedGroup);
        }
      },
      error: () => {
        this.groups = [];
        this.getFilteredPartners(this.selectedGroup);
      }
    });
  }

  findAndSelectGroupByName(categoryName: string): void {
    // Convert URL-friendly name back to match group titles
    const searchName = categoryName.replace(/-/g, ' ').toLowerCase();
    
    // Helper function to normalize text for comparison (handles Polish characters)
    const normalizeText = (text: string): string => {
      return text.toLowerCase()
        .replace(/ą/g, 'a')
        .replace(/ć/g, 'c')
        .replace(/ę/g, 'e')
        .replace(/ł/g, 'l')
        .replace(/ń/g, 'n')
        .replace(/ó/g, 'o')
        .replace(/ś/g, 's')
        .replace(/ź/g, 'z')
        .replace(/ż/g, 'z')
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    };
    
    const normalizedSearchName = normalizeText(searchName);
    
    // Find group by matching title
    const matchingGroup = this.groups.find(group => {
      const normalizedGroupTitle = normalizeText(group.title);
      return normalizedGroupTitle === normalizedSearchName ||
             group.title.toLowerCase() === searchName;
    });
    
    if (matchingGroup) {
      this.selectedGroup = matchingGroup.id;
      this.getFilteredPartners(this.selectedGroup);
    } else {
      // Try partial matching
      const partialMatch = this.groups.find(group => {
        const normalizedGroupTitle = normalizeText(group.title);
        return normalizedGroupTitle.includes(normalizedSearchName) ||
               normalizedSearchName.includes(normalizedGroupTitle) ||
               group.title.toLowerCase().includes(searchName) ||
               searchName.includes(group.title.toLowerCase());
      });
      
      if (partialMatch) {
        this.selectedGroup = partialMatch.id;
        this.getFilteredPartners(this.selectedGroup);
      } else {
        this.selectedGroup = null;
        this.getFilteredPartners(null);
      }
    }
  }

  ngAfterViewInit(): void {
    // Restore scroll position after view is initialized
    setTimeout(() => {
      const currentRoute = this.router.url; // Use actual current route instead of hardcoded '/partnerzy'
      this.scrollPositionService.restorePosition(currentRoute);
    }, 200);
  }
}
